/**
 * FormElements - Compact form inputs with industrial styling
 */

export function Input({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  error,
  className = '',
  ...props
}) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-slate-600 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`
          w-full px-2.5 py-1.5 text-sm
          border border-slate-200 rounded-sm
          bg-white text-slate-900 placeholder-slate-400
          focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400
          disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
          ${error ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : ''}
        `}
        {...props}
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export function Select({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  required = false,
  disabled = false,
  error,
  className = '',
  ...props
}) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-slate-600 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <select
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className={`
          w-full px-2.5 py-1.5 text-sm
          border border-slate-200 rounded-sm
          bg-white text-slate-900
          focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400
          disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
          ${error ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : ''}
        `}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export function Textarea({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  required = false,
  disabled = false,
  error,
  className = '',
  ...props
}) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-slate-600 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        required={required}
        disabled={disabled}
        className={`
          w-full px-2.5 py-1.5 text-sm
          border border-slate-200 rounded-sm
          bg-white text-slate-900 placeholder-slate-400
          focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400
          disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
          resize-none
          ${error ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : ''}
        `}
        {...props}
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  disabled = false,
  className = '',
}) {
  return (
    <label className={`inline-flex items-center gap-2 cursor-pointer ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="
          w-4 h-4 rounded-sm border-slate-300
          text-slate-700 focus:ring-slate-400 focus:ring-offset-0
        "
      />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}

// Form group for organizing form sections
export function FormGroup({ label, children, className = '' }) {
  return (
    <div className={`space-y-3 ${className}`}>
      {label && (
        <h4 className="text-sm font-medium text-slate-800 border-b border-slate-100 pb-2">
          {label}
        </h4>
      )}
      {children}
    </div>
  );
}

// Grid layout for forms
export function FormGrid({ columns = 2, gap = 4, children, className = '' }) {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid ${colClasses[columns] || colClasses[2]} gap-${gap} ${className}`}>
      {children}
    </div>
  );
}
